import { useCallback, useEffect, useRef } from 'react';
import { FullStory, init as initFullStory, isInitialized } from '@fullstory/browser';
import { FULLSTORY_ORG_ID as orgId } from '@bridebook/web/app-shared/lib/utils/constants';
import { getCurrentUserId } from '@bridebook/web/lib/users/selectors';
import { useSelector } from '@bridebook/web/lib/utils';

type FullStoryProperties = Record<string, unknown>;

/**
 * Sends properties of one scope once recording runs, and again only when they change.
 * Compared by value so an inline object does not re-send on every render.
 */
const useFullStoryProperties = (
  enabled: boolean,
  type: 'page' | 'user',
  properties?: FullStoryProperties,
) => {
  const sentProperties = useRef<string>();
  useEffect(() => {
    if (!enabled || !properties || !isInitialized()) return;
    const serialized = JSON.stringify(properties);
    if (sentProperties.current === serialized) return;
    sentProperties.current = serialized;
    FullStory('setProperties', { type, properties });
  }, [enabled, type, properties]);
};

/**
 * Initializes FullStory session recording and identifies the current user.
 * Recording is opt-in per caller via `enabled`. Sessions count against the
 * FullStory plan quota, so keep the enabling population narrow.
 * Optional page properties are attached to the recording so sessions can be
 * filtered in FullStory.
 */
export const useFullStory = (enabled: boolean = false, pageProperties?: FullStoryProperties) => {
  const userId = useSelector(getCurrentUserId);
  const isFullStoryInitialized = useRef(false);

  const identifyFullStoryUser = useCallback(() => {
    if (!userId) return;
    FullStory('setIdentity', {
      uid: userId,
    });
  }, [userId]);

  useEffect(() => {
    // Another caller on the page may have started recording already; one session serves all
    if (!enabled || isFullStoryInitialized.current || isInitialized()) return;
    isFullStoryInitialized.current = true;
    initFullStory({ orgId }, identifyFullStoryUser);
  }, [enabled, identifyFullStoryUser]);

  useFullStoryProperties(enabled, 'page', pageProperties);
};

// Properties stored on the recorded user, so sessions can be filtered by who is using the app
export const useFullStoryUserProperties = (enabled: boolean, properties: FullStoryProperties) =>
  useFullStoryProperties(enabled, 'user', properties);
